// ============================================================
// CLUBHUB – Einwilligungen und SEPA-Mandat: die Texte
// ============================================================
// EINE Quelle fuer Aufnahmeantrag, Mitgliederbereich und
// Eingangspruefung. Jede Einwilligung steht einzeln, mit einem Satz,
// den man ohne Jurastudium versteht, und dem vollen Text dahinter.
//
// Die Texte sind PLATZHALTER in sinnvoller Fassung. Den endgueltigen
// Wortlaut gibt der Verein vor (Datenschutzhinweise, Satzung);
// gespeichert wird je Erteilung die Fassung (`TEXTFASSUNG`), damit
// spaeter nachweisbar ist, wozu jemand ja gesagt hat.
// ============================================================
import { RW, wert } from "@/lib/rechtswerte";
import type { ConsentPurpose } from "@/generated/prisma/enums";
import type { ConsentKey } from "./views";

/** Fassung der Texte — geht mit jeder Erteilung in den Nachweis. */
export const TEXTFASSUNG = "2026-10";

export type Einwilligung = {
  key: ConsentKey;
  titel: string;
  /** Der eine Satz, der sagt, worum es geht. */
  kurz: string;
  /** Was passiert, wenn man ja sagt — und was, wenn nein. */
  lang: string;
  /** Freiwillig oder Voraussetzung der Mitgliedschaft. */
  pflicht: boolean;
  /** Lesestand an der Person (`Contact`). `null`: keine Einwilligung im Rechtssinn. */
  feld: "consentInternet" | "consentPhotos" | "consentNewsletter" | "consentPublishBirthday" | null;
  /**
   * Zweck im Verlauf (`Consent.purpose`). Die Mitgliederverwaltung hat
   * keinen: sie beruht auf der Mitgliedschaft (Art. 6 Abs. 1 lit. b),
   * nachgewiesen wird die Kenntnisnahme am Eingang (`SubmissionMeta`).
   */
  zweck: ConsentPurpose | null;
};

export const EINWILLIGUNGEN: Record<ConsentKey, Einwilligung> = {
  membership: {
    key: "membership",
    titel: "Datenschutzhinweise zur Mitgliederverwaltung",
    kurz: "Der Verein speichert Name, Anschrift, Kontakt- und Bankdaten, um die Mitgliedschaft zu führen.",
    lang:
      "Wir verarbeiten deine Angaben, um die Mitgliedschaft zu verwalten: Beiträge, Einladungen, Meldungen an den Landessportbund und die Fachverbände. Grundlage ist die Mitgliedschaft selbst (Art. 6 Abs. 1 lit. b DSGVO). Die Daten sehen nur Vorstand und die Personen mit Verwaltungsaufgaben. Nach dem Austritt werden sie gelöscht, sobald gesetzliche Aufbewahrungsfristen abgelaufen sind. Ohne diese Verarbeitung ist keine Mitgliedschaft möglich — ein Widerruf ist deshalb ein Austritt.",
    pflicht: true,
    feld: null,
    zweck: null,
  },
  internet: {
    key: "internet",
    titel: "Name auf Website und in sozialen Medien",
    kurz: "Dein Name darf auf der Vereinsseite und in den Kanälen des Vereins erscheinen, z. B. in Aufstellungen und Ergebnissen.",
    lang:
      "Mit dieser Einwilligung darf der Verein deinen Vor- und Nachnamen im Zusammenhang mit dem Vereinsleben veröffentlichen: Mannschaftsaufstellungen, Spielberichte, Ehrungen — auf der Website und auf Instagram oder Facebook. Ohne Einwilligung erscheinst du dort nur mit Vornamen und abgekürztem Nachnamen oder gar nicht. Du kannst jederzeit widerrufen; bereits Veröffentlichtes nimmt der Verein dann, soweit möglich, herunter.",
    pflicht: false,
    feld: "consentInternet",
    zweck: "INTERNET",
  },
  photos: {
    key: "photos",
    titel: "Fotos und Videos",
    kurz: "Fotos und Videos, auf denen du zu sehen bist, dürfen veröffentlicht werden.",
    lang:
      "Bei Spielen, Turnieren und Festen fotografiert der Verein. Mit dieser Einwilligung dürfen Bilder, auf denen du erkennbar bist, auf der Website, in sozialen Medien und in der Vereinszeitung erscheinen. Ohne Einwilligung achtet der Verein darauf, dich nicht erkennbar zu zeigen. Ein Widerruf gilt für die Zukunft; bereits gedruckte Ausgaben lassen sich nicht zurückholen.",
    pflicht: false,
    feld: "consentPhotos",
    zweck: "PHOTOS_PUBLIC",
  },
  newsletter: {
    key: "newsletter",
    titel: "Newsletter",
    kurz: "Du bekommst den Vereins-Newsletter mit Neuigkeiten, Terminen und Aktionen per E-Mail.",
    lang:
      "Etwa einmal im Monat: was im Verein los ist, Termine, Helferaufrufe, Aktionen von Sponsoren. Einladungen zur Mitgliederversammlung und Beitragsinformationen bekommst du unabhängig davon — die gehören zur Mitgliedschaft. Abbestellen geht jederzeit hier oder über den Link in jeder Ausgabe.",
    pflicht: false,
    feld: "consentNewsletter",
    zweck: "NEWSLETTER",
  },
  birthday: {
    key: "birthday",
    titel: "Geburtstag im Mitgliederbereich",
    kurz: "Andere Mitglieder sehen im Mitgliederbereich, wann du Geburtstag hast (ohne Jahrgang).",
    lang:
      "Der Verein gratuliert gern — im Mitgliederbereich und am Aushang im Vereinsheim, mit Tag und Monat, ohne Jahrgang. Ohne Einwilligung gratuliert nur der Vorstand persönlich.",
    pflicht: false,
    feld: "consentPublishBirthday",
    zweck: "BIRTHDAY_PUBLISH",
  },
};

export const EINWILLIGUNG_REIHENFOLGE: ConsentKey[] = ["membership", "internet", "photos", "newsletter", "birthday"];

/** Die Angaben des Vereins, die im Mandat stehen muessen. */
export type MandatsGlaeubiger = {
  name: string;
  anschrift: string;
  creditorId: string;
};

/**
 * Der Mandatstext einer SEPA-Basislastschrift — vollstaendig nach
 * dem Muster der Deutschen Kreditwirtschaft. `referenz` steht erst
 * nach der Aufnahme fest; bis dahin heisst es so im Text.
 *
 * Fristen NICHT fest: die Vorabankuendigung kommt aus der
 * Vereinseinstellung (`finanzen.vorabinfo_tage`), Vorgabe ist der
 * Rechtswert. Eine kuerzere Frist gilt nur, wenn sie IM MANDAT steht
 * — darum muessen Text und Einstellung dieselbe Zahl tragen.
 * BACKEND: `vorabTage` aus den Einstellungen des Vereins lesen.
 */
export function mandatsText(
  g: MandatsGlaeubiger,
  referenz: string | null,
  fristen: { vorabTage?: number; erstattungWochen?: number } = {},
): string[] {
  const vorab = fristen.vorabTage ?? wert(RW.SEPA_VORABANKUENDIGUNG_TAGE);
  const wochen = fristen.erstattungWochen ?? wert(RW.SEPA_RUECKGABE_AUTORISIERT_WOCHEN);
  const zahlwort: Record<number, string> = { 2: "zwei", 5: "fünf", 6: "sechs", 7: "sieben", 8: "acht", 10: "zehn", 14: "14" };
  return [
    `Zahlungsempfänger: ${g.name}, ${g.anschrift}`,
    `Gläubiger-Identifikationsnummer: ${g.creditorId}`,
    `Mandatsreferenz: ${referenz ?? "wird dir nach der Aufnahme mitgeteilt"}`,
    `SEPA-Lastschriftmandat: Ich ermächtige den ${g.name}, Zahlungen von meinem Konto mittels Lastschrift einzuziehen. Zugleich weise ich mein Kreditinstitut an, die vom ${g.name} auf mein Konto gezogenen Lastschriften einzulösen.`,
    `Hinweis: Ich kann innerhalb von ${zahlwort[wochen] ?? wochen} Wochen, beginnend mit dem Belastungsdatum, die Erstattung des belasteten Betrages verlangen. Es gelten dabei die mit meinem Kreditinstitut vereinbarten Bedingungen.`,
    `Der Verein kündigt jeden Einzug mindestens ${zahlwort[vorab] ?? vorab} Tage vorher mit Betrag und Datum an (Vorabankündigung). Das Mandat gilt für wiederkehrende Zahlungen: Mitgliedsbeiträge und Rechnungen des Vereins.`,
  ];
}

/** Die Frist der Vorabankuendigung in Tagen — fuer Hinweise ausserhalb des Mandatstexts. */
export function vorabTage(): number {
  return wert(RW.SEPA_VORABANKUENDIGUNG_TAGE);
}

/** Wie lange eine autorisierte Lastschrift zurueckgegeben werden kann, in Wochen. */
export function erstattungWochen(): number {
  return wert(RW.SEPA_RUECKGABE_AUTORISIERT_WOCHEN);
}
